import { z } from "zod"

import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import { platformSupportScopeV1Schema } from "./platform-workforce-v1.js"
import { tenantIdSchema } from "./identifiers.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{2,95}$/)

export const supportAccessGrantStateV1Schema = z.enum([
  "requested",
  "approved",
  "denied",
  "active",
  "expired",
  "revoked",
])

export const supportAccessGrantV1Schema = z.object({
  contractVersion: z.literal(1),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  beneficiaryPlatformOperatorId: z.uuid(),
  platformSupportCaseId: z.uuid(),
  reasonCode: codeSchema,
  scopes: z.array(platformSupportScopeV1Schema).min(1).max(5),
  sensitivity: z.enum(["metadata", "operational", "financial", "payroll", "security"]),
  downloadAllowed: z.boolean(),
  startsAt: timestampSchema,
  expiresAt: timestampSchema,
  state: supportAccessGrantStateV1Schema,
  requestedByPlatformOperatorId: z.uuid(),
  approvedByPlatformOperatorId: z.uuid().nullable(),
  activatedAt: timestampSchema.nullable(),
  revokedByPlatformOperatorId: z.uuid().nullable(),
  revocationReasonCode: codeSchema.nullable(),
  revision: z.number().int().positive().safe(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  createsTenantMembership: z.literal(false),
  createsFrappeDeskUser: z.literal(false),
  highRiskActionsDenied: z.literal(true),
}).strict().superRefine((grant, context) => {
  if (new Set(grant.scopes).size !== grant.scopes.length) {
    context.addIssue({ code: "custom", path: ["scopes"], message: "Grant scopes must be unique." })
  }
  if (Date.parse(grant.expiresAt) <= Date.parse(grant.startsAt)
      || Date.parse(grant.expiresAt) - Date.parse(grant.startsAt) > 8 * 60 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      path: ["expiresAt"],
      message: "Support access duration must be positive and no longer than eight hours.",
    })
  }
  if (grant.scopes.includes("tenant.export.download") !== grant.downloadAllowed) {
    context.addIssue({
      code: "custom",
      path: ["downloadAllowed"],
      message: "Download policy must exactly match the export scope.",
    })
  }
  const approved = grant.approvedByPlatformOperatorId !== null
  if (["approved", "active", "expired", "revoked"].includes(grant.state) && !approved) {
    context.addIssue({ code: "custom", message: "Approved grant states require a distinct approver." })
  }
  if (grant.approvedByPlatformOperatorId === grant.requestedByPlatformOperatorId
      || grant.approvedByPlatformOperatorId === grant.beneficiaryPlatformOperatorId) {
    context.addIssue({
      code: "custom",
      path: ["approvedByPlatformOperatorId"],
      message: "Requester or beneficiary cannot approve the same Support Access Grant.",
    })
  }
  if ((grant.state === "active" || grant.state === "expired" || grant.state === "revoked")
      !== (grant.activatedAt !== null)) {
    context.addIssue({
      code: "custom",
      path: ["activatedAt"],
      message: "Activation evidence must agree with the grant state.",
    })
  }
  if ((grant.state === "revoked") !== (grant.revokedByPlatformOperatorId !== null
      && grant.revocationReasonCode !== null)) {
    context.addIssue({ code: "custom", path: ["revocationReasonCode"], message: "Revocation must be attributed and reasoned." })
  }
})

const requestActionSchema = z.object({
  action: z.literal("request"),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  beneficiaryPlatformOperatorId: z.uuid(),
  platformSupportCaseId: z.uuid(),
  reasonCode: codeSchema,
  scopes: z.array(platformSupportScopeV1Schema).min(1).max(5),
  sensitivity: z.enum(["metadata", "operational", "financial", "payroll", "security"]),
  downloadAllowed: z.boolean(),
  startsAt: timestampSchema,
  expiresAt: timestampSchema,
}).strict()

const simpleAction = <T extends "approve" | "deny" | "activate" | "expire">(
  action: T,
) => z.object({
  action: z.literal(action),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
}).strict()

const revokeActionSchema = z.object({
  action: z.literal("revoke"),
  supportAccessGrantId: z.uuid(),
  targetTenantId: tenantIdSchema,
  reasonCode: codeSchema,
}).strict()

export const manageSupportAccessGrantActionV1Schema = z.discriminatedUnion("action", [
  requestActionSchema,
  simpleAction("approve"),
  simpleAction("deny"),
  simpleAction("activate"),
  revokeActionSchema,
  simpleAction("expire"),
])

export const manageSupportAccessGrantV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ManageSupportAccessGrant"),
  }),
  request: manageSupportAccessGrantActionV1Schema,
}).strict().superRefine((command, context) => {
  const requiresApproval = command.request.action === "approve"
  if (
    command.envelope.idempotencyNamespace !== "support.access_grant.manage"
    || command.envelope.target.scope !== "tenant"
    || command.envelope.target.targetTenantId !== command.request.targetTenantId
    || command.envelope.target.targetType !== "support_access_grant"
    || command.envelope.target.targetId !== command.request.supportAccessGrantId
    || (requiresApproval && command.envelope.requiredApprovalIds.length !== 1)
    || (!requiresApproval && command.envelope.requiredApprovalIds.length !== 0)
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope"],
      message: "Support Access Grant command must bind one exact Tenant, grant, and applicable approval.",
    })
  }
})

export const supportAccessUseDecisionV1Schema = z.object({
  responseVersion: z.literal(1),
  allowed: z.boolean(),
  code: z.enum([
    "allowed",
    "not_active",
    "expired",
    "operator_mismatch",
    "tenant_mismatch",
    "case_mismatch",
    "scope_denied",
    "download_denied",
    "high_risk_denied",
  ]),
  supportAccessGrantId: z.uuid(),
  effectiveExpiresAt: timestampSchema,
}).strict().superRefine((decision, context) => {
  if (decision.allowed !== (decision.code === "allowed")) {
    context.addIssue({ code: "custom", path: ["allowed"], message: "Decision and code must agree." })
  }
})

export type SupportAccessGrantV1 = z.infer<typeof supportAccessGrantV1Schema>
export type ManageSupportAccessGrantV1 = z.infer<typeof manageSupportAccessGrantV1Schema>
export type SupportAccessUseDecisionV1 = z.infer<typeof supportAccessUseDecisionV1Schema>
